import { useCallback } from 'react';
import { fetchArchiveLocation } from '../../../../utils/api';
import { formatPin } from '../../../../utils/pinFormat';

/**
 * 地図上のピン操作に関するイベントハンドラを集約するフック
 */
export const usePinHandlers = ({
    projectId,
    isEditMode,
    isReadOnly,
    tmpPin,
    setTmpPin,
    clearTmpPin,
    currentPin,
    setCurrentPin,
    clearCurrentPin,
    enableEdit,
    disableEdit,
    savePin,
    deletePin,
    uploadAll,
    tmpMediaList,
    tmpRemoveFileAt,
    clearMediaList,
    clearTmpMediaList,
    setIsUploadingMedia,
    setUploadProgress,
}) => {
    // マップクリック時の新規ピン作成（useMapEvents の onMapClick として使用）
    const handleNewPin = useCallback((lat, lng) => {
        if (isReadOnly || isEditMode || currentPin || tmpPin) return;
        setTmpPin({ title: '', description: '', position: { lat, lng }, labelId: null, thumbnail: null });
        clearMediaList();
        clearTmpMediaList();
        enableEdit();
    }, [isReadOnly, isEditMode, currentPin, tmpPin, setTmpPin, clearMediaList, clearTmpMediaList, enableEdit]);

    // マーカークリック時
    const handleMarkerClick = useCallback(async (marker) => {
        if (isEditMode) return;
        if (!marker?.id) return;
        clearMediaList();
        // 公開ページは認証必須APIを呼ばない。description は一覧に含まれないためそのまま表示
        if (isReadOnly || marker.description !== null || !projectId) {
            setCurrentPin(marker);
            return;
        }
        setCurrentPin(marker);
        try {
            const detail = await fetchArchiveLocation(projectId, marker.id);
            setCurrentPin(formatPin(detail));
        } catch {
            // 取得失敗時はそのまま表示（description なしで継続）
        }
    }, [isEditMode, isReadOnly, projectId, clearMediaList, setCurrentPin]);

    // エディター/ビューワーを閉じる
    const handleCloseEditor = useCallback(() => {
        clearTmpPin();
        clearCurrentPin();
        disableEdit();
        clearMediaList();
        clearTmpMediaList();
    }, [clearTmpPin, clearCurrentPin, disableEdit, clearMediaList, clearTmpMediaList]);

    // 新規ピンの内容を更新
    const handleUpdateTmpPin = useCallback((patch) => {
        setTmpPin((prev) => ({ ...prev, ...patch }));
    }, [setTmpPin]);

    // 既存ピンの内容を更新
    const handleUpdateCurrentPin = useCallback((patch) => {
        setCurrentPin((prev) => ({ ...prev, ...patch }));
    }, [setCurrentPin]);

    // 新規ピンをメディアとともに保存
    const handleSaveTmpPinWithMedia = useCallback(async () => {
        if (!tmpPin) return;

        if (tmpMediaList.length > 0) {
            const invalidItems = tmpMediaList.filter(item => !item.name || item.name.trim() === '');
            if (invalidItems.length > 0) {
                console.warn(`${invalidItems.length}個のメディアでタイトルが入力されていません。`);
                return;
            }
        }

        try {
            setIsUploadingMedia?.(true);
            setUploadProgress?.({ totalFiles: 0, completedFiles: 0 });
            const savedPin = await savePin(tmpPin);

            if (savedPin && tmpMediaList.length > 0) {
                try {
                    await uploadAll(tmpMediaList, savedPin.id, (progress) => {
                        setUploadProgress?.(progress);
                    });
                } catch {
                    // ピンは保存済みなのでエラーを再投げしない
                }
            }

            clearTmpPin();
            disableEdit();
            clearMediaList();
        } catch {
            // エラー時は状態をクリアしない（ユーザーが再試行できるように）
        } finally {
            setIsUploadingMedia?.(false);
            setUploadProgress?.(null);
        }
    }, [tmpPin, tmpMediaList, savePin, uploadAll, clearTmpPin, disableEdit, clearMediaList, setIsUploadingMedia, setUploadProgress]);

    // 既存ピンを保存（メディアは MarkerEditorContainer 内で処理）
    const handleSaveCurrentPinWithMedia = useCallback(async () => {
        if (!currentPin) return;

        try {
            setIsUploadingMedia?.(true);
            await savePin(currentPin);
            clearCurrentPin();
            disableEdit();
            clearMediaList();
        } catch {
            // エラー時は状態をクリアしない
        } finally {
            setIsUploadingMedia?.(false);
        }
    }, [currentPin, savePin, clearCurrentPin, disableEdit, clearMediaList, setIsUploadingMedia]);

    // 既存ピンを削除
    const handleDeleteCurrentPin = useCallback(async () => {
        if (!currentPin?.id) return;

        try {
            await deletePin(currentPin.id);
            clearCurrentPin();
            disableEdit();
            clearMediaList();
        } catch {
            // usePinActions 内で toast 表示済み
        }
    }, [currentPin, deletePin, clearCurrentPin, disableEdit, clearMediaList]);

    // 新規ピンのメディア削除（tmpPin は id なし → 未アップロードファイルのみ対象）
    const handleDeleteTmpPinMedia = useCallback((mediaId, markerId, isNewFile = false, fileIndex = null) => {
        if (isNewFile && fileIndex !== null) {
            tmpRemoveFileAt(fileIndex);
        }
    }, [tmpRemoveFileAt]);

    return {
        handleNewPin,
        handleMarkerClick,
        handleCloseEditor,
        handleUpdateTmpPin,
        handleUpdateCurrentPin,
        handleSaveTmpPinWithMedia,
        handleSaveCurrentPinWithMedia,
        handleDeleteCurrentPin,
        handleDeleteTmpPinMedia,
    };
};
